import { useCallback, useEffect, useState } from "react";

import { Badge, Card, Menu, ScrollArea, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useFetcher, useNavigate } from "@remix-run/react";
import {
  IconBell,
  IconBellRingingFilled,
  IconMail,
  IconMailCheck,
} from "@tabler/icons-react";

import { useMapStore } from "../GenericMap/store/useMapStore";

import { useStore } from "~/hooks/zustend";
import type { NotificationListItem } from "~/types/notification";
import { getRouteWithCurrentHubId } from "~/utils/hubid";

type NotificationsType = {
  hubId: string;
  notifications: NotificationListItem[];
};

export const Notification = ({ hubId, notifications }: NotificationsType) => {
  const [opened, { toggle: toogleNotificationMenu }] = useDisclosure();
  const { setActiveId } = useMapStore();
  const { setCurrentHubId } = useStore();
  const [notificationsData, setNotificationsData] =
    useState<NotificationListItem[]>(notifications);
  const [count, setCount] = useState(0);
  const notificationsFetcher = useFetcher<{
    notifications: NotificationListItem[];
  }>();

  const navigate = useNavigate();

  useEffect(() => {
    setNotificationsData(notifications);
  }, [notifications]);

  useEffect(() => {
    if (notificationsFetcher.data?.notifications) {
      setNotificationsData(notificationsFetcher.data.notifications);
    }
  }, [notificationsFetcher.data]);

  const refreshNotifications = useCallback(() => {
    if (notificationsFetcher.state === "idle") {
      notificationsFetcher.load("/api/notifications");
    }
  }, [notificationsFetcher]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshNotifications();
      }
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshNotifications]);

  const getPulseAnimation = () => ({
    animation: `pulse 1s ease-out 1s 10`,
  });

  const getCountPendingNotifications = useCallback(() => {
    return notificationsData?.filter((item) => !item.acknowledge)?.length || 0;
  }, [notificationsData]);

  useEffect(() => {
    setCount(getCountPendingNotifications());
  }, [notificationsData, getCountPendingNotifications]);

  const onClickNotification = async (notification: NotificationListItem) => {
    toogleNotificationMenu();

    if (!notification.acknowledge) {
      await fetch(`/hub/${hubId}/notification/acknowledge/${notification?.id}`);
    }

    setNotificationsData((prevData) =>
      prevData.map((notify) =>
        notify.id === notification?.id
          ? { ...notify, acknowledge: true }
          : notify,
      ),
    );

    if (notification.actionId) {
      switch (notification?.type) {
        case "action-plan":
          setCurrentHubId(notification.hubId);
          setActiveId(notification.actionId);
          navigate(
            getRouteWithCurrentHubId({
              route: `/action-plans/items`,
              currentHubId: notification.hubId,
            }),
          );
          break;
        case "task":
          setCurrentHubId(notification.hubId);
          navigate(
            getRouteWithCurrentHubId({
              route: `tasks?taskId=${notification.actionId}`,
              currentHubId: notification.hubId,
            }),
          );
          break;
        default:
          break;
      }
    }
  };

  function formatDateAgo(date: string): string {
    const now = new Date();
    const inputDate = new Date(date);
    const diffInMs = now.getTime() - inputDate.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min`;
    } else if (diffInHours < 24) {
      return `${diffInHours} h`;
    } else if (diffInDays < 30) {
      return `${diffInDays} d`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} m`;
    } else {
      return `${diffInYears} y`;
    }
  }

  const AcknowledgeIcon = ({ acknowledge }: { acknowledge: boolean }) => {
    return (
      <div style={{ position: "absolute", right: 15, top: 10 }}>
        {acknowledge ? (
          <IconMailCheck color="gray" size={20} />
        ) : (
          <IconMail color="orange" size={20} />
        )}
      </div>
    );
  };

  return (
    <Menu
      width={400}
      withArrow
      position="bottom-end"
      transitionProps={{ transition: "pop-top-right" }}
      onClose={toogleNotificationMenu}
      onOpen={() => {
        toogleNotificationMenu();
        refreshNotifications();
      }}
      opened={opened}
    >
      <Menu.Target>
        {count ? (
          <div
            style={{
              position: "relative",
              width: 40,
              marginTop: 10,
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              verticalAlign: "center",
            }}
          >
            <IconBellRingingFilled
              cursor={"pointer"}
              color="var(--mantine-color-orange-filled)"
              style={getPulseAnimation()}
            />
            <Badge
              size={"sm"}
              color="red"
              circle
              style={{ position: "absolute", top: -5, right: 4 }}
            >
              {getCountPendingNotifications()}
            </Badge>
          </div>
        ) : (
          <IconBell cursor={"pointer"} />
        )}
      </Menu.Target>
      <Menu.Dropdown spellCheck={true} style={{ marginTop: 15 }}>
        <ScrollArea
          style={{
            height: notificationsData.length * 110,
            minHeight: 110,
            maxHeight: 320,
          }}
        >
          {notificationsData.length === 0 && (
            <div style={{ justifyItems: "center", marginTop: 40 }}>
              <Text>You don't have any notifications at the moment.</Text>
            </div>
          )}
          {notificationsData.map((notification) => (
            <Card
              key={notification.id}
              shadow="md"
              padding={"sm"}
              component="button"
              onClick={() => onClickNotification(notification)}
              style={{
                marginTop: 15,
                border: "none",
                cursor: "pointer",
                width: "100%",
              }}
            >
              <AcknowledgeIcon acknowledge={notification?.acknowledge} />
              <Text fw={600} size="sm" style={{ textAlign: "left" }}>
                {notification.title}
              </Text>
              <Text mt="xs" c="dimmed" size="xs" style={{ textAlign: "left" }}>
                {notification.description}
              </Text>
              <Text size="xs" c="dimmed" style={{ marginTop: 5 }}>
                {formatDateAgo(notification.createdAt)}
              </Text>
            </Card>
          ))}
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  );
};
